import React, { useCallback, useEffect, useMemo, useState } from "react"

import { FeatureFlagsExistenceLogicalOperator } from "@common-shared/config/featureFlags.config"
import { AvailableLookupConsumer } from "@common-shared/config/lookup.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { ButtonElement } from "@front-shared/elements/button.element"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { useCptCodes } from "@front-shared/hooks/cptCodes.hook"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import {
    CoverageCheckerMode,
    CoverageCheckerRunningState,
} from "../../config/checker.config"
import { FEATURE_FLAGS_CONFIG } from "../../config/featureFlags.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { CheckerHelper } from "../../helpers/checker.helper"
import { PermissionsHelper } from "../../helpers/permissions.helper"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { useDemoMode } from "../../hooks/useDemoMode.hook"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { checkerSelector } from "../../store/selectors/checker.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSelector } from "../../store/selectors/userDetails.selector"
import {
    checkerReset,
    checkerResetWithKeepingInputData,
} from "../../store/slices/checker.slice"
import { coveragePayersGetList } from "../../store/thunks/coveragePayers.thunks"
import { OrganizationQuoteComponent } from "../general/quotaChecksProgressComponent.component"
import { CoverageCheckerCSVComponent } from "./csv/coverageCheckerCSV.component"
import { CoverageCheckerManualComponent } from "./manual/coverageCheckerManual.component"
import { ResultsDownloadComponent } from "./resultsDownload.component"
import { RunningTypeSelectorComponent } from "./runningTypeSelector.component"

import refreshIcon from "@front-shared/assets/images/icons/refresh-dark2.svg"

export const CoverageCheckerComponent: React.FunctionComponent = () => {
    useScrollFix()

    const dispatch = useAppDispatch()

    const [manualCheckerKey, setManualCheckerKey] = useState(
        new Date().getTime(),
    )

    const isMobile = useIsMobile()

    const profile = useAppSelector(profileSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { selectedPracticeUsageQuota } = useAppSelector(userDetailsSelector)
    const checkerState = useAppSelector(checkerSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const { isDemo } = useDemoMode()

    useIsFeatureFlagEnabled(
        FEATURE_FLAGS_CONFIG.checkerFeatureFlags,
        FeatureFlagsExistenceLogicalOperator.OR,
        ROUTES_CONFIG.profileUrl,
    )

    const { availableCptCodes } = useCptCodes(
        AvailableLookupConsumer.coveragePortal,
        selectedPracticeRole?.availableModalities,
    )

    const canRunCoverageChecksByPermissions = useMemo<boolean>(
        () =>
            PermissionsHelper.hasPermission(
                [
                    OneVerifyPermission.runCoverageChecks,
                    OneVerifyPermission.runCsvBulkCoverageChecks,
                ],
                selectedPracticeRole,
            ),
        [selectedPracticeRole],
    )

    // If received more than X results -> stop loader, so user can preview results already
    // Also show spinner if processing CSV right now
    const isLoading = useMemo<boolean>(() => {
        const continuousMonitoringResultsCount =
            checkerState.continuousMonitoringCoverageChecks.filter(
                item => !!item.result,
            ).length

        const medicaidResultsCount = checkerState.medicaidChecks.filter(
            item => !!item.result,
        ).length

        const isBulkChecksResultsPending =
            continuousMonitoringResultsCount <= 5 && medicaidResultsCount <= 5

        return (
            checkerState.runningState ===
                CoverageCheckerRunningState.PROCESS_CSV ||
            (RuntimeHelper.isBulkCoverageChecksLoading() &&
                isBulkChecksResultsPending)
        )
    }, [
        runtimeState.isLoading,
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.runningState,
    ])

    // Listen for browser back btn -> reset Coverage Checker page
    useEffect(() => {
        window.addEventListener("popstate", onReset)

        return () => {
            window.removeEventListener("popstate", onReset)
        }
    }, [])

    // Load fresh coverage payers when selectedPracticeRole changes
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            coveragePayersGetList({
                payload: undefined,
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [selectedPracticeRole?.id])

    const onReset = () => {
        dispatch(checkerReset())
    }

    const onResetWithKeepingInputData = () => {
        dispatch(checkerResetWithKeepingInputData())
    }

    const onResetManualChecker = useCallback(() => {
        setManualCheckerKey(new Date().getTime())

        dispatch(checkerReset())
    }, [dispatch])

    const isResultsView = useMemo<boolean>(() => {
        return [
            CoverageCheckerRunningState.RESULTS_MANUAL,
            CoverageCheckerRunningState.RESULTS_CSV,
        ].includes(checkerState.runningState)
    }, [checkerState.runningState])

    // Matches when CoverageCheckerCSVComponent renders the bulk insights layout,
    // which owns its own progress and result summaries
    const isBulkResultsInsightsView = useMemo<boolean>(() => {
        if (
            checkerState.checkerMode !== CoverageCheckerMode.CSV ||
            isDemo ||
            [
                CoverageCheckerRunningState.INPUT_CSV,
                CoverageCheckerRunningState.PROCESS_CSV,
            ].includes(checkerState.runningState)
        ) {
            return false
        }

        return (
            checkerState.continuousMonitoringCoverageChecks.some(
                item => !!item.result,
            ) || checkerState.medicaidChecks.some(item => !!item.result)
        )
    }, [
        checkerState.checkerMode,
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.medicaidChecks,
        checkerState.runningState,
        isDemo,
    ])

    const headerTitle = useMemo<string>(() => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.PREVIEW_CSV:
                return "Preview Checks"

            default:
                return "Nirvana Coverage Checker"
        }
    }, [checkerState.runningState])

    const headerSubtitle = useMemo<JSX.Element | null>(() => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.PREVIEW_CSV:
                return (
                    <>
                        You can remove records before you run the checks.
                        <br className="md:hidden" /> Note that{" "}
                        <span className="text-bold">
                            any checks with errors will not be included
                        </span>{" "}
                        in the run.
                    </>
                )

            default:
                return null
        }
    }, [checkerState.runningState])

    // For csv checker during preview show how many quota will be used
    const csvInProgressQuotaUsage = useMemo<number>(() => {
        if (
            checkerState.runningState !==
                CoverageCheckerRunningState.PREVIEW_CSV ||
            checkerState.coverageChecksSubmitted
        ) {
            return 0
        }

        return CheckerHelper.getInProgressChecksQuotaAmount(
            checkerState.csvCheckType,
            checkerState.continuousMonitoringCoverageChecks,
            checkerState.medicaidChecks,
            selectedPracticeUsageQuota,
        )
    }, [
        selectedPracticeUsageQuota,
        checkerState.runningState,
        checkerState.csvCheckType,
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.medicaidChecks,
        checkerState.coverageChecksSubmitted,
    ])

    const headerActions =
        checkerState.runningState !== CoverageCheckerRunningState.PREVIEW_CSV &&
        (!isDemo || isResultsView) ? (
            <div
                className={`
                    flex items-center mt-[24px]
                    ${isBulkResultsInsightsView ? "justify-end" : ""}
                `}
            >
                {!isDemo && !isBulkResultsInsightsView ? (
                    <RunningTypeSelectorComponent className="flex-1" />
                ) : null}

                {isResultsView ? (
                    <ButtonElement
                        label="Run another check(s)"
                        type="default"
                        size="large"
                        icon={refreshIcon}
                        htmlType="button"
                        onClick={onResetWithKeepingInputData}
                    />
                ) : null}
            </div>
        ) : null

    return runtimeState.enabledFeatureFlags ? (
        <div className="pb-32 relative">
            <PageHelmetElement
                title="Coverage Checker"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <NirvanaBreathingLoaderElement
                isActive={isLoading && !isBulkResultsInsightsView}
            />

            {!profile || !availableCptCodes.length ? (
                <SpinnerElement containerClassName="text-center mt-[48px]" />
            ) : canRunCoverageChecksByPermissions ? (
                <div>
                    <div className="max-w-[760px]">
                        <PrimaryText
                            className={isMobile ? "mt-[72px]" : ""}
                            typography={isMobile ? "h2" : "h1"}
                        >
                            {headerTitle}
                        </PrimaryText>

                        {headerSubtitle ? (
                            <PrimaryText weight="medium" className="mt-[8px]">
                                {headerSubtitle}
                            </PrimaryText>
                        ) : null}

                        <OrganizationQuoteComponent
                            className="mt-[16px]"
                            inProgressQuotaUsage={csvInProgressQuotaUsage}
                            collapsible={!csvInProgressQuotaUsage}
                        />

                        {!isBulkResultsInsightsView ? headerActions : null}
                    </div>

                    {isBulkResultsInsightsView ? headerActions : null}

                    {!isLoading &&
                    checkerState.checkerMode !== CoverageCheckerMode.CSV ? (
                        <ResultsDownloadComponent />
                    ) : null}

                    {checkerState.checkerMode === CoverageCheckerMode.CSV &&
                    !isDemo ? (
                        <CoverageCheckerCSVComponent />
                    ) : (
                        <CoverageCheckerManualComponent
                            key={manualCheckerKey}
                            resetForm={onResetManualChecker}
                        />
                    )}
                </div>
            ) : null}
        </div>
    ) : (
        <SpinnerElement containerClassName="text-center mt-[48px]" />
    )
}
